import {
  DocumentListPaginatedDocument,
  DocumentListCreateDocument,
  DocumentListGetByIdDocument,
  DocumentListDeleteDocument,
  DocumentListUpdateDocument,
  DocumentListListDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  DocumentListByIdResponse,
  IDocumentListArgs,
  IDocumentListModel,
  IDocumentListResponse,
  IDocumentModel,
} from "api/services/library/document-list";
import { Optional } from "config";
import {
  NotificationDuration,
  NotificationMode,
  showSnackBar,
  store,
} from "store";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  ICreateInvestmentDocumentListArgs,
  IInvestmentDocumentListUpdateArgs,
} from "./types";

export async function getDocumentsList(
  variables?: IDocumentListArgs
): Promise<IDocumentListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: DocumentListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.documentListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createDocumentList(
  variables: ICreateInvestmentDocumentListArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DocumentListCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all document list queries so they refetch with the new item
    executeEviction(client, {
      typename: "DocumentList",
      fieldNames: ["documentListPaginated", "documentListList"],
    });

    return res?.data?.documentListCreate;
  } catch (error: any) {
    store.dispatch(
      showSnackBar({
        duration: NotificationDuration.ERRORS,
        message: error.message,
        mode: NotificationMode.ERROR,
      })
    );
    throw error;
  }
}

export async function getDocumentsListById(
  documentListId: number
): Promise<DocumentListByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: DocumentListGetByIdDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.documentListGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteDocumentList(
  documentListId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DocumentListDeleteDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "DocumentList",
      id: documentListId.toString(),
      fieldNames: [
        "documentListPaginated",
        "documentListList",
        "documentListGetById",
      ],
    });

    return Boolean(res?.data?.documentListDelete);
  } catch (error) {
    throw error;
  }
}

export async function updateDocumentList(
  variables: IInvestmentDocumentListUpdateArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DocumentListUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "DocumentList",
      id: variables.documentListId.toString(),
      fieldNames: [
        "documentListPaginated",
        "documentListList",
        "documentListGetById",
      ],
    });

    return res?.data?.documentListUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getDocumentListsNames(): Promise<
  Array<
    Pick<IDocumentListModel, "name" | "documentListId"> & {
      documents: Pick<IDocumentModel, "documentId" | "name">[];
    }
  >
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: DocumentListListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.documentListList ?? [];
  } catch (error) {
    throw error;
  }
}
